import * as React from "react";

import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { useMutation } from "@redwoodjs/web";

import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import { allIntegrationTypes } from "src/lib/constants";
import { SYNC_INTEGRATION_MUTATION } from "src/lib/queries/integrations";

export function DataSyncSection({ store, type }) {
  const [syncIntegration, { loading: syncIntegrationLoading }] = useMutation(
    SYNC_INTEGRATION_MUTATION,
    {
      onCompleted: ({ syncIntegration }) => {
        const type = allIntegrationTypes[syncIntegration.type.toLowerCase()];
        toast.info("Starting data sync", {
          description:
            "Your " +
            (type.nameLong || "integration") +
            " data is being synced. This may take up to an hour",
        });
      },
      onError: (error) => {
        toast.error("Error syncing integration", {
          description: error?.message || "Please try again",
        });
      },
    }
  );

  const syncBlockedByConnection = store && !store.isConnected;
  const syncDisabled =
    store?.syncing || syncIntegrationLoading || syncBlockedByConnection;
  const syncDescription = syncBlockedByConnection
    ? store.connectionStatusReason
    : syncDisabled
      ? "Your store is currently syncing. Please be patient as this process may take up to an hour due to Shopify limitations."
      : `Manually synchronize your ${type.nameLong} data. This will update all connected integrations and refresh your analytics.`;
  const syncButtonLabel = syncBlockedByConnection
    ? "Sync unavailable"
    : syncDisabled
      ? "Syncing in process..."
      : "Sync Store Data";

  const [open, setOpen] = React.useState(false);

  const handleConfirm = () => {
    syncIntegration({
      variables: { id: store.id },
    });
    setOpen(false);
  };

  return (
    <div className="flex flex-col items-start justify-between gap-4 rounded-md border p-4 sm:flex-row sm:items-center">
      <div className="text-foreground flex w-full flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">Sync store data</span>
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/5 px-2 py-0.5 text-xs font-medium text-amber-500">
            <ShieldAlert className="size-3" />
            DEV only
          </span>
        </div>
        <div className="text-muted-foreground text-sm">{syncDescription}</div>
        <div className="text-muted-foreground text-xs leading-5">
          Hidden from store users and store admins. Do not direct customers to
          use it; only DEV users can see and run this action.
        </div>
      </div>
      <div className="flex h-full w-full items-end justify-end sm:w-fit">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" disabled={syncDisabled} className="gap-2">
              {syncButtonLabel}
              {syncDisabled && !syncBlockedByConnection ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Data Sync</DialogTitle>
              <DialogDescription>
                Starting a manual sync will temporarily disable access to this
                store. Due to Shopify limitations, the sync process may take
                several hours to complete. Are you sure you want to proceed?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleConfirm} disabled={syncIntegrationLoading}>
                Yes, Start Sync
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
